/** @jsxImportSource @emotion/react */
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from '../../assets/logo.svg';
import { usePlausible } from '../../../hooks/usePlausible';
import { keyframes } from '@emotion/react';

// Define the animation
const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

interface PlausibleEvents {
  'High Interest Form View': undefined;
  'High Interest Form Submitted': undefined;
  [key: string]: any;  // Add index signature for other events
}

function HighInterestCandidateApplicationComponent() {
  const { trackEvent } = usePlausible<PlausibleEvents>();
  const startTime = React.useRef(Date.now());
  const navigate = useNavigate();

  // Track initial form view
  useEffect(() => {
    trackEvent('High Interest Form View', undefined, 0);
  }, [trackEvent]);

  // Handle direct navigation to the thank you page
  const handleContinue = () => {
    try {
      trackEvent('High Interest Form Submitted', undefined, 0);
      navigate('/apply/thank-you');
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div className="min-h-screen bg-[#7B1C14] py-8 px-4 sm:px-6 lg:px-8 font-sans">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header Section */}
        <div className="text-center mb-4">
          <img 
            src={logo} 
            alt="Spicy Cubes" 
            className="h-16 mx-auto"
          />
          <h1 className="text-2xl font-bold text-[#F7E8CC] mt-4">Creator Opportunity: Final Stage</h1>
        </div>
        
        <div className="bg-[#6E1912] rounded-lg shadow-lg p-6">
          <div className="bg-[#5E150F] rounded-lg p-4 mb-4 border-2 border-[#FFD700] shadow-lg">
            <h2 className="text-xl font-bold text-center text-[#FFD700]">
              Get $30 for Your Try-Out Video!
            </h2>
            <p className="text-center mt-2 text-[#F7E8CC]">
              Recreate one of these 3 proven videos and submit it. You will receive $30 upon submission and be considered for hire.
            </p>
          </div>
          
          <div className="space-y-4 text-[#F7E8CC]/90">
            <p className="font-bold text-center text-[#FFD700]">
              💰 Copy one of these viral videos! 💰
            </p>
            
            <div className="space-y-4">
              {/* Example 1 */}
              <div className="bg-[#5E150F]/70 p-4 rounded-md">
                <h4 className="font-bold text-[#FFD700] mb-1">1. "You've been warned ⚠️"</h4>
                <p className="mb-1">
                  <a 
                    href="https://www.tiktok.com/@wantspicycubes/video/7394602679563029791?_r=1&_t=8rwL66OFxvS" 
                    className="text-[#FF851B] hover:underline" 
                    target="_blank" 
                    rel="noopener noreferrer">
                    Watch Example →
                  </a>
                </p>
                <p className="text-sm">
                  Hook: <span className="font-bold">"Almost got fired because of my husband..."</span>
                </p>
              </div>
              
              {/* Example 2 */}
              <div className="bg-[#5E150F]/70 p-4 rounded-md">
                <h4 className="font-bold text-[#FFD700] mb-1">2. "I need advice…"</h4>
                <p className="mb-1">
                  <a 
                    href="https://www.tiktok.com/@wantspicycubes/video/7470582626303085855" 
                    className="text-[#FF851B] hover:underline" 
                    target="_blank" 
                    rel="noopener noreferrer">
                    Watch Example →
                  </a>
                </p>
                <p className="text-sm">
                  Hook: <span className="font-bold">"Need advice on what I should do about this situation..."</span>
                </p>
              </div>
              
              {/* Example 3 */}
              <div className="bg-[#5E150F]/70 p-4 rounded-md">
                <h4 className="font-bold text-[#FFD700] mb-1">3. "Am I wrong for with-holding?"</h4>
                <p className="mb-1">
                  <a 
                    href="https://www.tiktok.com/@wantspicycubes/video/7482203836363394350" 
                    className="text-[#FF851B] hover:underline" 
                    target="_blank" 
                    rel="noopener noreferrer">
                    Watch Example →
                  </a>
                </p>
                <p className="text-sm">
                  Hook: <span className="font-bold">"Am I wrong for withholding this from my boyfriend?"</span>
                </p>
              </div>
              
              <div className="p-3 bg-[#5E150F] rounded-md text-center">
                <p className="text-[#FFD700] font-medium">Requirements:</p>
                <ul className="text-sm mt-1 list-disc pl-5 text-left">
                  <li>Video must be at least 1:30 long</li>
                  <li>Video must not look like a sales pitch, it should be a story</li>
                  <li>Mention Spicy Cubes (aphrodisiac gummies) near the end</li>
                </ul>
              </div>
              
              <div className="p-3 bg-[#5E150F] rounded-md text-center">
                <p className="text-[#FFD700] font-medium">Tips for Success:</p>
                <ul className="text-sm mt-1 list-disc pl-5 text-left">
                  <li>Talk like you're FaceTiming a friend</li>
                  <li>Create suspense to keep viewers watching</li>
                  <li>Film in a natural setting (car, walking, etc.)</li>
                  <li>Include small details that spark comments</li>
                  <li>Note: You do not need the product in the video</li>
                </ul>
              </div>
            </div>
            
            <div className="mt-4 text-center">
              <a 
                href="https://forms.gle/qdC67TSmMFHJTFDc8" 
                target="_blank" 
                rel="noopener noreferrer"
                css={{
                  animation: `${pulse} 2s ease-in-out infinite`,
                }}
                className="inline-flex justify-center rounded-md bg-[#FFB700] py-3 px-6 text-lg font-bold text-[#5E150F] shadow-sm hover:bg-[#FFA200] focus:outline-none focus:ring-2 focus:ring-[#FFD700]"
              >
                📹 Submit Your Video Here 📹
              </a>
            </div>
          </div>
        </div>

        <div className="text-center">
          <p className="text-[#F7E8CC]/80">
            Questions? Contact{' '}
            <a 
              href="mailto:ashli@spicycubes.co" 
              className="font-bold text-[#F7E8CC] underline"
            >
              ashli@spicycubes.co
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

export const HighInterestCandidateApplication = HighInterestCandidateApplicationComponent; 